import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * TypewriterHeadline — headline animado da hero da home (efeito
 * máquina de escrever) alternando entre múltiplas definições da
 * plataforma.
 *
 * ## Formato de ``phrases``
 * Cada item pode ser:
 *   - Uma string simples (sem destaque, sem link).
 *   - Um objeto ``{ text, highlight?, href?, ariaLabel? }``:
 *       • ``text``: frase completa a digitar.
 *       • ``highlight``: substring de ``text`` que ganha estilo
 *         de accent (vermelho itálico). Case-sensitive, primeira
 *         ocorrência apenas.
 *       • ``href``: rota interna do react-router. Quando presente,
 *         toda a frase vira ``<Link to={href}>``. O href muda em
 *         sincronia com o ciclo de frases.
 *       • ``ariaLabel``: aria-label do link (fallback: ``text``).
 *
 * ## Decisões de UX/perf
 * 1. **Zero impacto no LCP**: o estado inicial já renderiza a
 *    primeira frase completa. O React pinta o H1 no primeiro paint
 *    exatamente como se fosse estático — o efeito de digitação só
 *    começa depois de ``initialDelay`` (padrão 3000ms).
 * 2. **Prefers-reduced-motion**: usuários com preferência de
 *    movimento reduzido veem apenas a primeira frase estática, sem
 *    ciclo — respeita acessibilidade e evita ansiedade visual.
 * 3. **Sem dependência externa**: React puro, ~0.5KB gzipped.
 * 4. **Cursor piscante via CSS puro** (``animate-pulse`` Tailwind).
 * 5. **Layout stability**: caller deve reservar ``min-h`` no wrapper
 *    proporcional à frase mais longa.
 */
export default function TypewriterHeadline({
  phrases,
  typeSpeed = 50,
  backSpeed = 30,
  backDelay = 2500,
  initialDelay = 3000,
  className = "",
  cursorClassName = "",
  linkClassName = "",
  testId = "typewriter-headline",
  // ``isStatic``: renderiza a 1ª frase completa, sem digitação nem
  // cursor — H1 fixo para SEO (exigência do editor, Out/2026).
  isStatic = false,
}) {
  // Normaliza para o formato objeto uma vez só.
  const norm = useMemo(
    () => (phrases || []).map((p) => (typeof p === "string" ? { text: p } : p)),
    [phrases]
  );

  const [text, setText] = useState(norm[0]?.text || "");
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [active, setActive] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const timerRef = useRef(null);

  // Quando a lista de frases muda (API do admin), a 1ª frase é
  // reaplicada por inteiro — vale para o modo estático e para o
  // animado antes do kickoff.
  useEffect(() => {
    if (!active) setText(norm[0]?.text || "");
  }, [norm, active]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const listener = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", listener);
    return () => mq.removeEventListener?.("change", listener);
  }, []);

  useEffect(() => {
    if (reducedMotion || isStatic) return undefined;
    const kickoff = setTimeout(() => setActive(true), initialDelay);
    return () => clearTimeout(kickoff);
  }, [initialDelay, reducedMotion, isStatic]);

  useEffect(() => {
    if (!active || reducedMotion || norm.length === 0) return undefined;
    const current = norm[phraseIdx]?.text || "";

    if (!deleting && text === current) {
      timerRef.current = setTimeout(() => setDeleting(true), backDelay);
      return () => clearTimeout(timerRef.current);
    }
    if (deleting && text === "") {
      setDeleting(false);
      setPhraseIdx((idx) => (idx + 1) % norm.length);
      return undefined;
    }
    const nextText = deleting
      ? current.slice(0, text.length - 1)
      : current.slice(0, text.length + 1);
    const speed = deleting ? backSpeed : typeSpeed;
    timerRef.current = setTimeout(() => setText(nextText), speed);
    return () => clearTimeout(timerRef.current);
  }, [active, deleting, text, phraseIdx, norm, typeSpeed, backSpeed, backDelay, reducedMotion]);

  // Renderização com destaque: divide o texto TYPED em segmentos
  // pré-highlight / highlight / pós-highlight. A substring de
  // destaque é fixa por frase (posicionada no ``text`` original),
  // então progride junto com a digitação.
  const currentPhrase = norm[phraseIdx] || { text: "" };
  const rendered = renderTypedWithHighlight(text, currentPhrase.text, currentPhrase.highlight);

  // Cursor com animação via animate-pulse do Tailwind + estilo
  // inline pra garantir dimensões proporcionais ao font-size.
  const cursor = (
    <span
      aria-hidden="true"
      className={`inline-block ml-1 w-[0.08em] align-baseline animate-pulse ${cursorClassName}`}
      style={{ height: "0.9em", background: "currentColor", verticalAlign: "-0.05em" }}
    />
  );

  const body = (
    <>
      <span aria-live={isStatic ? undefined : "polite"} aria-atomic="true">
        {rendered}
      </span>
      {!isStatic && cursor}
    </>
  );

  // Se a frase atual tem ``href``, envolvemos em Link. Trocar
  // ``phraseIdx`` faz o React reconstruir o <Link> com novo ``to`` —
  // não há flash de href errado.
  if (currentPhrase.href) {
    return (
      <Link
        to={currentPhrase.href}
        aria-label={currentPhrase.ariaLabel || currentPhrase.text}
        data-testid={testId}
        className={`group inline-block transition-colors hover:opacity-95 ${className} ${linkClassName}`}
      >
        {body}
        <ArrowUpRight
          aria-hidden="true"
          className="inline-block align-baseline ml-2 w-[0.5em] h-[0.5em] opacity-40 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
          style={{ verticalAlign: "0.15em" }}
        />
      </Link>
    );
  }

  return (
    <span data-testid={testId} className={className}>
      {body}
    </span>
  );
}

/**
 * Divide o texto atualmente digitado em segmentos preservando o
 * destaque. Se ``highlight`` está presente e visível dentro do
 * ``typed``, retorna 3 spans (pré / highlight / pós); caso
 * contrário, retorna o typed inteiro como texto simples.
 */
function renderTypedWithHighlight(typed, fullText, highlight) {
  if (!highlight) return typed;
  const idx = fullText.indexOf(highlight);
  if (idx < 0) return typed;
  const end = idx + highlight.length;
  // Caso 1: ainda não começou a digitar o highlight
  if (typed.length <= idx) return typed;
  // Caso 2: já passou completamente pelo highlight
  if (typed.length >= end) {
    return (
      <>
        {typed.slice(0, idx)}
        <span className="italic text-red-500">{typed.slice(idx, end)}</span>
        {typed.slice(end)}
      </>
    );
  }
  // Caso 3: estamos DENTRO do highlight (digitação parcial)
  return (
    <>
      {typed.slice(0, idx)}
      <span className="italic text-red-500">{typed.slice(idx)}</span>
    </>
  );
}
